import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ToastProvider } from '@/components/ui/toast'
import { queryClient } from '@/lib/query-client'
import { AppGate } from '@/app-gate'
import '@/styles/theme.css'

/**
 * Provider order is not cosmetic:
 *
 *  1. `QueryClientProvider` — the router's components read the cache during
 *     render, so it has to be the outermost of the three.
 *  2. `TooltipProvider` — one Radix provider for the whole app gives grouped
 *     hover-delay skipping: the first tooltip waits, the next ones under the
 *     same sweep appear instantly. Per-leaf providers lose that.
 *  3. `ToastProvider` — inside the tooltip provider so a toast can contain a
 *     tooltipped control, outside the router so a toast survives navigation.
 *  4. `AppGate` — resolves the session, then mounts the router. It is inside
 *     the query provider because it asks a query, and outside the router
 *     because it decides whether the router mounts at all.
 *
 * StrictMode stays on. It double-invokes effects in development, which is
 * exactly the pressure `useRealtime()` needs: a hook that opens an
 * `EventSource` must survive mount → unmount → mount without leaking a socket,
 * and StrictMode is what proves it does.
 */

const container = document.getElementById('root')

if (!container) {
  throw new Error('Root container #root is missing from index.html')
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ToastProvider>
          <AppGate />
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>
)
